import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink, Nav, NavItem, Navbar, NavbarBrand, NavbarToggler, Collapse } from 'reactstrap';
import { NavLink as RouterNavLink, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ToggleMenu } from '../store/MenuAction';
import SubFooter from './SubFooter';
import NavSection from '../util/NavSection';
import { FlowSyncAccessPolicy, isAuthorized } from '../models/FlowSyncAccessPolicy';
import { CoreMyModuleTypes, ModuleTypeName } from '../models/CoreModels';
import { useTranslation } from 'react-i18next';
import { RequestState } from '../models/RequestState';
import StatusCode from '../util/StatusCode';
import { RootState } from '../store/store';
import { CoreLoggedInUser } from '../models/CoreLoggedInUser';

interface NavMenuProps {
    activeSection?: string
}

const NavMenu: React.FC<NavMenuProps> = (props) => {
    const { t } = useTranslation(['common']);
    const dispatch = useDispatch();
    const location = useLocation();

    const { activeSection } = props;

    const [collapsed, setCollpased] = useState(true);

    const open = useSelector<RootState, { [key: string]: boolean }>(state => state.menu.open);
    const authorizedAccessPolicies = useSelector<RootState, FlowSyncAccessPolicy[]>(state => state.me.me?.authorizedAccessPolicies ?? []);
    const myModuleTypes = useSelector<RootState, RequestState<CoreMyModuleTypes>>(state => state.module.myModuleTypes);
    const me = useSelector<RootState, CoreLoggedInUser | undefined>(state => state.me.me);
    const masterUrl = me?.masterUrl
    const customerId = me?.customerId;


    useEffect(() => {
        if (props.activeSection) {
            ToggleMenu(props.activeSection)(dispatch);
        }
    }, [dispatch])

    const toggleNavbar = useCallback(() => {
        setCollpased(!collapsed);
    }, [collapsed]);

    const toggleMenu = useCallback((name: string) => {
        ToggleMenu(name)(dispatch);
    }, [dispatch]);

    const renderSchoolAdmin = useMemo(() => {
        if (!isAuthorized(authorizedAccessPolicies, FlowSyncAccessPolicy.READ)) {
            return false;
        }
        if (myModuleTypes.code === StatusCode.COMPLETE) {
            return myModuleTypes.data.schoolAdmin;
        }
        return false;
    }, [authorizedAccessPolicies, myModuleTypes])

    const renderScheduleSync = useMemo(() => {
        if (!isAuthorized(authorizedAccessPolicies, FlowSyncAccessPolicy.READ)) {
            return false;
        }
        if (myModuleTypes.code === StatusCode.COMPLETE) {
            return myModuleTypes.data.scheduleSync;
        }
        return false;
    }, [authorizedAccessPolicies, myModuleTypes])

    const renderOneRoster = useMemo(() => {
        if (!isAuthorized(authorizedAccessPolicies, FlowSyncAccessPolicy.READ)) {
            return false;
        }
        if (myModuleTypes.code === StatusCode.COMPLETE) {
            return myModuleTypes.data.oneRoster;
        }
        return false;
    }, [authorizedAccessPolicies, myModuleTypes])

    const renderConfiguration = useMemo(() => {
        return isAuthorized(authorizedAccessPolicies, FlowSyncAccessPolicy.READ);
    }, [authorizedAccessPolicies])

    const renderPasswordChange = useMemo(() => {
        return isAuthorized(authorizedAccessPolicies, FlowSyncAccessPolicy.CHANGE_PASSWORDS)
            && myModuleTypes.code === StatusCode.COMPLETE
            && myModuleTypes.data.licensedModuleTypeNames.includes(ModuleTypeName.FlowSyncChangePassword);
    }, [authorizedAccessPolicies, myModuleTypes]);

    const renderPasswordManagement = useMemo(() => {
        return isAuthorized(authorizedAccessPolicies, FlowSyncAccessPolicy.EXPORT_UNEXPORTED_PASSWORD) || renderPasswordChange || isAuthorized(authorizedAccessPolicies, FlowSyncAccessPolicy.EDIT_PASSWORD_SETTINGS)
    }, [authorizedAccessPolicies, renderPasswordChange])

    const renderDevice = useMemo(() => {
        if (!isAuthorized(authorizedAccessPolicies, FlowSyncAccessPolicy.GOOGLE_DEVICE_READ)) {
            return false;
        }
        if (myModuleTypes.code === StatusCode.COMPLETE) {
            return myModuleTypes.data.deviceManager;
        }
        return false;
    }, [authorizedAccessPolicies, myModuleTypes])

    const renderClassroomAdmin = useMemo(() => {
        if (!isAuthorized(authorizedAccessPolicies, FlowSyncAccessPolicy.CLASSROOM_ADMIN_READ)) {
            return false;
        }
        if (myModuleTypes.code === StatusCode.COMPLETE) {
            return myModuleTypes.data.classroomAdminClassroom;
        }
        return false;
    }, [authorizedAccessPolicies, myModuleTypes])

    const activeTab = (url: string) => {
        return location.pathname.startsWith(url);
    }

    const symbols = useCallback((name: string) => {
        return open[name] ?
            <FontAwesomeIcon icon="chevron-down" className="arrow" /> :
            <FontAwesomeIcon icon="chevron-right" className="arrow" />;
    }, [open])

    const active = useCallback((section: string) =>
        activeSection === section && !open[section] ? 'active' : ''
        , [activeSection, open])

    return (
        <Navbar className="dark">
            <NavbarBrand className="mr-auto brand"><img src="/FlowSync_lg_light.svg" alt="FlowSync" className="logo" /></NavbarBrand>
            <NavbarToggler onClick={toggleNavbar} ><FontAwesomeIcon icon="bars" /> </NavbarToggler>
            <Collapse isOpen={!collapsed} navbar>
                <Nav navbar key={location.key}>
                    <NavItem>
                        <NavLink tag={RouterNavLink} to="/"><FontAwesomeIcon icon="network-wired" className="nav_icon" />{t('nav.dashboard', { ns: 'common' })}</NavLink>
                    </NavItem>
                    <NavItem>
                        <NavLink tag={RouterNavLink} to="/overview"><FontAwesomeIcon icon="diagram-project" className="nav_icon" />{t('nav.syncOverview', { ns: 'common' })}</NavLink>
                    </NavItem>

                    {renderPasswordManagement && <>
                        <NavItem onClick={() => toggleMenu(NavSection.PASSWORDADMINISTRATION)}>
                            <NavLink href="#" className={active(NavSection.PASSWORDADMINISTRATION)}>{t('nav.passwordAdministration', { ns: 'common' })}<span className="open">{symbols(NavSection.PASSWORDADMINISTRATION)}</span></NavLink>
                        </NavItem>
                        {open[NavSection.PASSWORDADMINISTRATION] && <>
                            {isAuthorized(authorizedAccessPolicies, FlowSyncAccessPolicy.EXPORT_UNEXPORTED_PASSWORD) &&
                                <NavItem>
                                    <NavLink active={activeTab('/passwordadmin/passwords')} tag={RouterNavLink} to="/passwordadmin/passwords" className="submenu"><FontAwesomeIcon icon="key" className="nav_icon" />{t('object.passwordAdmin.passwordDistribution', { ns: 'common' })}</NavLink>
                                </NavItem>
                            }
                            {renderPasswordChange &&
                                <NavItem>
                                    <NavLink active={activeTab('/passwordadmin/passwordchange')} tag={RouterNavLink} to="/passwordadmin/passwordchange" className="submenu"><FontAwesomeIcon icon="user-lock" className="nav_icon" />{t('object.passwordAdmin.passwordChange', { ns: 'common' })}</NavLink>
                                </NavItem>
                            }
                            {isAuthorized(authorizedAccessPolicies, FlowSyncAccessPolicy.EDIT_PASSWORD_SETTINGS) &&
                                <NavItem>
                                    <NavLink active={activeTab('/passwordadmin/configuration')} tag={RouterNavLink} to="/passwordadmin/configuration" className="submenu"><FontAwesomeIcon icon="rectangle-list" className="nav_icon" />{t('object.passwordAdmin.passwordSettings', { ns: 'common' })}</NavLink>
                                </NavItem>
                            }
                        </>}
                    </>}

                    {renderClassroomAdmin && <React.Fragment>

                        <NavItem onClick={() => toggleMenu(NavSection.CLASSROOMADMIN)}>
                            <NavLink href="#" className={active(NavSection.CLASSROOMADMIN)}>{t('nav.classroomAdministration', { ns: 'common' })}<span className="open">{symbols(NavSection.CLASSROOMADMIN)}</span></NavLink>
                        </NavItem>
                        {open[NavSection.CLASSROOMADMIN] && <>
                            <NavItem>
                                <NavLink active={activeTab('/classroomadmin/dashboard')} tag={RouterNavLink} to="/classroomadmin/dashboard" className="submenu">
                                    <FontAwesomeIcon icon="network-wired" className="nav_icon" />
                                    {t('nav.overview', { ns: 'common' })}
                                </NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink active={activeTab('/classroomadmin/classroom')} tag={RouterNavLink} to="/classroomadmin/classroom" className="submenu"> <FontAwesomeIcon icon="chalkboard" className="nav_icon" />{t('object.classroomAdmin.classroom', { ns: 'common' })}</NavLink>
                            </NavItem>
                        </>}
                    </React.Fragment>}

                    {renderSchoolAdmin && <React.Fragment>
                        <NavItem onClick={() => toggleMenu(NavSection.SCHOOLADMINISTRATION)}>
                            <NavLink href="#" className={active(NavSection.SCHOOLADMINISTRATION)}>{t('nav.schoolAdmin', { ns: 'common' })}<span className="open">{symbols(NavSection.SCHOOLADMINISTRATION)}</span></NavLink>
                        </NavItem>
                        {open[NavSection.SCHOOLADMINISTRATION] && <React.Fragment>
                            <NavItem>
                                <NavLink active={activeTab('/dashboard')} tag={RouterNavLink} to="/dashboard/" className="submenu"><FontAwesomeIcon icon="network-wired" className="nav_icon" />{t('nav.overview', { ns: 'common' })}</NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink active={activeTab('/schoolunit')} tag={RouterNavLink} to="/schoolunit/" className="submenu"><FontAwesomeIcon icon="school" className="nav_icon" />{t('object.schoolAdmin.school', { ns: 'common', count: 2 })}</NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink active={activeTab('/activity')} tag={RouterNavLink} to="/activity/" className="submenu"><FontAwesomeIcon icon="calendar-alt" className="nav_icon" />{t('object.schoolAdmin.activity', { ns: 'common', count: 2 })}</NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink active={activeTab('/group')} tag={RouterNavLink} to="/group/" className="submenu"><FontAwesomeIcon icon="users" className="nav_icon" />{t('object.schoolAdmin.group', { ns: 'common', count: 2 })}</NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink active={activeTab('/user')} tag={RouterNavLink} to="/user/" className="submenu"><FontAwesomeIcon icon="user" className="nav_icon" />{t('object.schoolAdmin.person', { ns: 'common', count: 2 })}</NavLink>
                            </NavItem>
                        </React.Fragment>}
                    </React.Fragment>}


                    {renderScheduleSync && <React.Fragment>
                        <NavItem onClick={() => toggleMenu(NavSection.SCHEDULESYNC)}>
                            <NavLink href="#" className={active(NavSection.SCHEDULESYNC)}>{t('nav.scheduleSync', { ns: 'common' })}<span className="open">{symbols(NavSection.SCHEDULESYNC)}</span></NavLink>
                        </NavItem>
                        {open[NavSection.SCHEDULESYNC] && <React.Fragment>
                            <NavItem>
                                <NavLink tag={RouterNavLink} active={activeTab('/schedulesync/user')} to="/schedulesync/user/" className="submenu"><FontAwesomeIcon icon="user" className="nav_icon" />{t('object.scheduleSync.user', { ns: 'common', count: 2 })}</NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink tag={RouterNavLink} active={activeTab('/schedulesync/event')} to="/schedulesync/event/" className="submenu"><FontAwesomeIcon icon="calendar-alt" className="nav_icon" />{t('object.scheduleSync.calendarEvent', { ns: 'common', count: 2 })}</NavLink>
                            </NavItem>
                        </React.Fragment>}
                    </React.Fragment>}


                    {renderOneRoster && <React.Fragment>
                        <NavItem onClick={() => toggleMenu(NavSection.ONEROSTER)}>
                            <NavLink href="#" className={active(NavSection.ONEROSTER)}>{t('nav.oneRoster', { ns: 'common' })}<span className="open">{symbols(NavSection.ONEROSTER)}</span></NavLink>
                        </NavItem>
                        {open[NavSection.ONEROSTER] && <React.Fragment>
                            <NavItem>
                                <NavLink tag={RouterNavLink} active={activeTab('/oneroster/dashboard')} to="/oneroster/dashboard" className="submenu"><FontAwesomeIcon icon="network-wired" className="nav_icon" />{t('nav.overview', { ns: 'common' })}</NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink tag={RouterNavLink} active={activeTab('/oneroster/schoolunit')} to="/oneroster/schoolunit" className="submenu"><FontAwesomeIcon icon="school" className="nav_icon" />{t('object.oneRoster.school', { ns: 'common', count: 2 })}</NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink tag={RouterNavLink} active={activeTab('/oneroster/class')} to="/oneroster/class" className="submenu"><FontAwesomeIcon icon="calendar-alt" className="nav_icon" />{t('object.oneRoster.section', { ns: 'common', count: 2 })}</NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink tag={RouterNavLink} active={activeTab('/oneroster/user')} to="/oneroster/user" className="submenu"><FontAwesomeIcon icon="user" className="nav_icon" />{t('object.oneRoster.user', { ns: 'common', count: 2 })}</NavLink>
                            </NavItem>
                        </React.Fragment>}
                    </React.Fragment>}

                    {renderDevice && <>
                        <NavItem onClick={() => toggleMenu(NavSection.DEVICES)}>
                            <NavLink href="#" className={active(NavSection.DEVICES)}>{t('nav.googleDevices', { ns: 'common' })}<span className="open">{symbols(NavSection.DEVICES)}</span></NavLink>
                        </NavItem>
                        {open[NavSection.DEVICES] && <>
                            <NavItem>
                                <NavLink tag={RouterNavLink} active={activeTab('/device/dashboard')} to="/device/dashboard" className="submenu"><FontAwesomeIcon icon="network-wired" className="nav_icon" />{t('nav.overview', { ns: 'common' })}</NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink tag={RouterNavLink} active={activeTab('/device/googleDevices')} to="/device/googleDevices" className="submenu"><FontAwesomeIcon icon="laptop" className="nav_icon" />{t('object.googleDevice.device', { ns: 'common' })}</NavLink>
                            </NavItem>
                        </>}
                    </>}

                    {renderConfiguration && <React.Fragment>
                        <NavItem onClick={() => toggleMenu(NavSection.CONFIGURATION)}>
                            <NavLink href="#" className={active(NavSection.CONFIGURATION)}>{t('nav.configuration', { ns: 'common' })}<span className="open">{symbols(NavSection.CONFIGURATION)}</span></NavLink>
                        </NavItem>
                        {open[NavSection.CONFIGURATION] && <React.Fragment>
                            <NavItem>
                                <NavLink tag={RouterNavLink} active={activeTab('/module')} to="/module/" className="submenu"><FontAwesomeIcon icon="file-import" className="nav_icon" />{t('nav.modules', { ns: 'common' })}</NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink tag={RouterNavLink} to="/instanceConfig/" className="submenu"><FontAwesomeIcon icon="cog" className="nav_icon" />{t('nav.instanceSettings', { ns: 'common' })}</NavLink>
                            </NavItem>
                            {isAuthorized(authorizedAccessPolicies, FlowSyncAccessPolicy.AUDIT_LOG_READ) &&
                                <NavItem>
                                    <NavLink tag={RouterNavLink} to="/auditlog/" className="submenu"><FontAwesomeIcon icon="list" className="nav_icon" />{t('nav.auditLog', { ns: 'common' })}</NavLink>
                                </NavItem>
                            }
                            {isAuthorized(authorizedAccessPolicies, FlowSyncAccessPolicy.ROLE_ASSIGN) &&
                                <NavItem>
                                    <NavLink tag="a" href={`${masterUrl}/permissions/${customerId}`} target="_blank" rel="noopener noreferrer" className="submenu"><FontAwesomeIcon icon="user-lock" className="nav_icon" />{t('nav.roleAssign', { ns: 'common' })}</NavLink>
                                </NavItem>
                            }
                            {isAuthorized(authorizedAccessPolicies, FlowSyncAccessPolicy.ROLE_ADMIN) &&
                                <NavItem>
                                    <NavLink tag="a" href={`${masterUrl}/roles`} target="_blank" rel="noopener noreferrer" className="submenu"><FontAwesomeIcon icon="user-shield" className="nav_icon" />{t('nav.roleAdmin', { ns: 'common' })}</NavLink>
                                </NavItem>
                            }
                        </React.Fragment>}
                    </React.Fragment>}

                    <NavItem>
                        <SubFooter className="nav" />
                    </NavItem>
                </Nav>
            </Collapse>
        </Navbar>
    );
}

export default NavMenu;
